import '../shared-panels.css';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import './recommendation-picker.css';
import {
  Search,
  X,
  Check,
  Plus,
  Trash2,
  SlidersHorizontal,
  RefreshCw,
  ExternalLink,
  Layers,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { sourceProvider } from '../../../core/SourceProvider.ts';
import { sourceHttpClient } from '../../../core/SourceHttp.ts';
import { installedAnimeSources, createAnimeSource } from '../../../anime/AnimeRepository.ts';
import type { YomiriManga } from '../../../types/extension.ts';
import type { HomeMedia } from '../../../metadata/HomeRecommendations.ts';
import { mediaTitle } from '../../../metadata/HomeRecommendations.ts';
import { yomiriDatabase } from '../../../storage/YomiriDatabase.ts';
import { coverImageUrl, coverReferrerPolicy } from '../coverReferrerPolicy.ts';
import { recommendationMatch, recommendationQueries, normalizedTitle, searchRecommendation, type RecommendationResult } from './RecommendationSearch.ts';

type Source = { id: string; name: string };

export function RecommendationPicker({
  item,
  onClose,
  onOpenManga,
  onNavigate,
  anchorEl,
  onPick,
}: {
  item: HomeMedia | null;
  onClose: () => void;
  onOpenManga: (id: string, chapter?: string, manga?: YomiriManga) => void;
  onNavigate: (view: string) => void;
  anchorEl?: HTMLElement | null;
  onPick?: (result: RecommendationResult) => void;
}) {
  const [sources, setSources] = useState<Source[]>([]);
  const [results, setResults] = useState<RecommendationResult[]>([]);
  const [linkedList, setLinkedList] = useState<RecommendationResult[]>([]);
  const [query, setQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'search' | 'linked'>('search');
  const attempted = useRef(new Set<string>());
  const version = useRef(0);

  // Load installed sources & saved confirmed links
  useEffect(() => {
    const current = ++version.current;
    setResults([]);
    setLinkedList([]);
    setMessage('');
    setBusy(false);
    setSources([]);
    setReady(false);
    setSelectedSource('');
    attempted.current.clear();

    const term = item ? item.title.romaji || mediaTitle(item) : '';
    setQuery(term);

    if (!item) return;

    void (async () => {
      try {
        const available: Source[] =
          item.type === 'MANGA'
            ? sourceProvider
                .getEnabledExtensions()
                .filter((source) => !['yomiri-mock-source', 'bridged-mal'].includes(source.id))
                .map((source) => ({ id: source.id, name: source.name }))
            : (await installedAnimeSources()).map((id) => ({
                id,
                name: createAnimeSource(id, sourceHttpClient).name,
              }));

        if (current !== version.current) return;
        setSources(available);
        setReady(true);

        // Read all confirmed links for this work
        const confirmed = (await yomiriDatabase.getSetting<RecommendationResult[]>(
          `confirmed-links:${item.type}:${item.id}`,
          []
        )) || [];
        const singleLink = await yomiriDatabase.getSetting<RecommendationResult>(
          `home-link:${item.type}:${item.id}`
        );

        const mergedLinked = [...confirmed];
        if (singleLink && !mergedLinked.some((r) => r.sourceId === singleLink.sourceId && r.id === singleLink.id)) {
          mergedLinked.push(singleLink);
        }

        if (current === version.current) {
          setLinkedList(mergedLinked);
          // If we have confirmed links, start by showing them or perform search
          if (mergedLinked.length > 0) {
            setActiveTab('linked');
          } else {
            setActiveTab('search');
            // Auto search first 2 sources
            void executeSearch(item, available.slice(0, 2), term, current);
          }
        }
      } catch {
        if (current === version.current) {
          setReady(true);
          setMessage('No se pudieron consultar tus fuentes instaladas.');
        }
      }
    })();

    return () => {
      ++version.current;
    };
  }, [item]);

  const executeSearch = async (work: HomeMedia, batch: Source[], term: string, current: number) => {
    if (!batch.length || current !== version.current || !term.trim()) return;
    setBusy(true);
    setMessage('');
    let failures = 0;

    await Promise.all(
      batch.map(async (source) => {
        attempted.current.add(source.id);
        try {
          const rows: RecommendationResult[] = [];
          for (const alias of recommendationQueries(work, term)) {
            if (current !== version.current) return;
            const found = await searchRecommendation(work.type, source.id, alias);
            rows.push(
              ...found.filter(
                (row) =>
                  recommendationMatch(row, work) > 0 ||
                  normalizedTitle(row.title).includes(normalizedTitle(term))
              )
            );
            if (rows.some((row) => recommendationMatch(row, work) === 3)) break;
          }
          if (current === version.current) {
            setResults((previous) => [
              ...new Map([...previous, ...rows].map((row) => [row.sourceId + ':' + row.id, row])).values(),
            ]);
          }
        } catch {
          failures++;
        }
      })
    );

    if (current === version.current) {
      setBusy(false);
      if (failures && failures === batch.length) {
        setMessage('Las fuentes seleccionadas no devolvieron resultados para este término.');
      }
    }
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!item || !query.trim() || busy) return;
    const current = ++version.current;
    attempted.current.clear();
    setResults([]);
    setActiveTab('search');

    const targetSources = selectedSource
      ? sources.filter((s) => s.id === selectedSource)
      : sources;

    void executeSearch(item, targetSources, query.trim(), current);
  };

  // Add / Confirm a source result
  const linkSource = async (result: RecommendationResult) => {
    if (!item) return;
    try {
      // 1. Add to confirmed-links list
      const existing =
        (await yomiriDatabase.getSetting<RecommendationResult[]>(`confirmed-links:${item.type}:${item.id}`, [])) || [];
      const updated = [...new Map([...existing, result].map((r) => [r.sourceId + ':' + r.id, r])).values()];
      await yomiriDatabase.setSetting(`confirmed-links:${item.type}:${item.id}`, updated);

      // 2. Set primary link & unified-link
      await yomiriDatabase.setSetting(`home-link:${item.type}:${item.id}`, result);
      await yomiriDatabase.setSetting(`home-link-confirmed:${item.type}:${item.id}`, result.sourceId + ':' + result.id);
      if (item.type === 'ANIME') {
        await yomiriDatabase.setSetting(`unified-link:${item.id}:${result.sourceId}`, result);
      }
      await yomiriDatabase.setSetting(`home-preferred-source:${item.type}`, result.sourceId);

      setLinkedList(updated);
      onPick?.(result);
    } catch {
      setMessage('No se pudo vincular la fuente. Reintentá.');
    }
  };

  // Remove a linked source (if user made a mistake)
  const unlinkSource = async (result: RecommendationResult) => {
    if (!item) return;
    try {
      const existing =
        (await yomiriDatabase.getSetting<RecommendationResult[]>(`confirmed-links:${item.type}:${item.id}`, [])) || [];
      const filtered = existing.filter((r) => !(r.sourceId === result.sourceId && r.id === result.id));
      await yomiriDatabase.setSetting(`confirmed-links:${item.type}:${item.id}`, filtered);

      // Clean single link if it was the one removed
      const currentPrimary = await yomiriDatabase.getSetting<RecommendationResult>(
        `home-link:${item.type}:${item.id}`
      );
      if (currentPrimary?.sourceId === result.sourceId && currentPrimary?.id === result.id) {
        if (filtered.length > 0) {
          await yomiriDatabase.setSetting(`home-link:${item.type}:${item.id}`, filtered[0]);
          await yomiriDatabase.setSetting(`home-link-confirmed:${item.type}:${item.id}`, filtered[0].sourceId + ':' + filtered[0].id);
        } else {
          await yomiriDatabase.deleteSetting(`home-link:${item.type}:${item.id}`);
          await yomiriDatabase.deleteSetting(`home-link-confirmed:${item.type}:${item.id}`);
        }
      }

      if (item.type === 'ANIME') {
        await yomiriDatabase.deleteSetting(`unified-link:${item.id}:${result.sourceId}`);
      }

      setLinkedList(filtered);
      onPick?.(filtered[0] || result);
    } catch {
      setMessage('No se pudo desvincular la fuente.');
    }
  };

  const isLinked = (result: RecommendationResult) => {
    return linkedList.some((r) => r.sourceId === result.sourceId && r.id === result.id);
  };

  const sourceName = (id: string) => sources.find((s) => s.id === id)?.name || id;

  const orderedResults = useMemo(() => {
    if (!item) return [];
    return results
      .filter((row) => !selectedSource || row.sourceId === selectedSource)
      .slice()
      .sort((a, b) => recommendationMatch(b, item) - recommendationMatch(a, item) || a.title.localeCompare(b.title));
  }, [results, selectedSource, item]);

  return (
    <Dialog
      className="yomiri-panel"
      open={!!item}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          className: 'y-src-dialog-paper',
          'aria-label': item ? `Configurar fuentes para ${mediaTitle(item)}` : 'Fuentes',
        },
      }}
    >
      {/* Header */}
      <Box className="y-src-header">
        {item && (
          <img
            src={item.coverImage.large}
            alt=""
            referrerPolicy="no-referrer"
            className="y-src-header-cover"
          />
        )}
        <div className="y-src-header-meta">
          <span className="y-src-badge">CONFIGURACIÓN DE FUENTES</span>
          <DialogTitle sx={{ p: 0, fontSize: 18, fontWeight: 700, color: '#ffffff', lineHeight: 1.3 }}>
            {item ? mediaTitle(item) : ''}
          </DialogTitle>
          <span className="y-src-subtitle">
            Buscá y vinculá una o varias fuentes para cargar episodios o capítulos
          </span>
        </div>
        <button className="y-src-close-btn" aria-label="Cerrar ventana" onClick={onClose}>
          <X size={16} />
        </button>
      </Box>

      {/* Tabs */}
      <div className="y-src-tabs-bar">
        <button
          type="button"
          className={`y-src-tab-item ${activeTab === 'search' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          <Search size={14} />
          <span>Buscar y vincular</span>
        </button>
        <button
          type="button"
          className={`y-src-tab-item ${activeTab === 'linked' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('linked')}
        >
          <Layers size={14} />
          <span>Fuentes vinculadas ({linkedList.length})</span>
        </button>
      </div>

      <DialogContent className="y-src-content">
        {/* Search Bar Form */}
        {activeTab === 'search' && (
          <>
            <form className="y-src-search-form" onSubmit={handleSearchSubmit}>
              <div className="y-src-field-title">
                <label htmlFor="y-src-query-input">Título a buscar</label>
                <div className="y-src-input-wrapper">
                  <Search size={14} className="y-src-input-icon" />
                  <input
                    id="y-src-query-input"
                    value={query}
                    placeholder="Ej. Frieren, Shingeki..."
                    onChange={(e) => setQuery(e.target.value)}
                    className="y-src-input"
                  />
                  {query && (
                    <button
                      type="button"
                      className="y-src-clear-btn"
                      onClick={() => setQuery('')}
                      aria-label="Limpiar búsqueda"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>

              <div className="y-src-field-source">
                <label htmlFor="y-src-provider-select">Fuente específica</label>
                <select
                  id="y-src-provider-select"
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="y-src-select"
                >
                  <option value="">Todas las fuentes activas</option>
                  {sources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={busy || !query.trim()}
                className="nyomiri-btn nyomiri-btn-primary y-src-submit-btn"
              >
                {busy ? <CircularProgress size={14} color="inherit" /> : <Search size={14} />}
                <span>Buscar</span>
              </button>
            </form>

            {message && (
              <div className="y-src-message-banner" role="status">
                <AlertCircle size={14} />
                <span>{message}</span>
              </div>
            )}

            {/* Results List */}
            <div className="y-src-results-container">
              <div className="y-src-results-header">
                <span>Resultados encontrados ({orderedResults.length})</span>
                {busy && <span className="y-src-searching-tag">Consultando proveedores...</span>}
              </div>

              {orderedResults.length > 0 ? (
                <div className="y-src-results-list">
                  {orderedResults.map((res) => {
                    const linked = isLinked(res);
                    const matchScore = item ? recommendationMatch(res, item) : 0;
                    return (
                      <div
                        key={res.sourceId + ':' + res.id}
                        className={`y-src-result-card ${linked ? 'is-linked' : ''}`}
                      >
                        {res.coverUrl ? (
                          <img
                            src={coverImageUrl(res.coverUrl)}
                            referrerPolicy={coverReferrerPolicy(res.coverUrl)}
                            alt=""
                            className="y-src-card-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="y-src-cover-placeholder" />
                        )}

                        <div className="y-src-card-info">
                          <strong className="y-src-card-title">{res.title}</strong>
                          <div className="y-src-card-tags">
                            <span className="y-src-source-tag">{sourceName(res.sourceId)}</span>
                            {matchScore === 3 && (
                              <span className="y-src-match-tag">Coincidencia exacta</span>
                            )}
                            {linked && <span className="y-src-active-tag">Vinculado</span>}
                          </div>
                        </div>

                        <div className="y-src-card-actions">
                          {linked ? (
                            <button
                              type="button"
                              className="y-src-btn-unlink"
                              title="Desvincular esta fuente"
                              onClick={() => void unlinkSource(res)}
                            >
                              <Check size={14} />
                              <span>Vinculada</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="y-src-btn-link"
                              title="Vincular fuente a esta obra"
                              onClick={() => void linkSource(res)}
                            >
                              <Plus size={14} />
                              <span>Vincular</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                !busy && (
                  <div className="y-src-empty-state">
                    <p>
                      {sources.length === 0
                        ? 'No tenés fuentes instaladas. Podés instalar proveedores desde la sección Fuentes.'
                        : 'No se encontraron resultados aún. Probá escribiendo otro nombre o seleccionando una fuente específica.'}
                    </p>
                  </div>
                )
              )}
            </div>
          </>
        )}

        {/* Linked Sources Tab (Allows reviewing & unlinking easily) */}
        {activeTab === 'linked' && (
          <div className="y-src-linked-tab">
            <p className="y-src-section-description">
              Estas son las fuentes vinculadas a esta obra. Yomiri unifica los episodios y capítulos
              automáticamente de todas las fuentes confirmadas aquí.
            </p>

            {linkedList.length > 0 ? (
              <div className="y-src-results-list">
                {linkedList.map((res) => (
                  <div key={res.sourceId + ':' + res.id} className="y-src-result-card is-linked">
                    {res.coverUrl ? (
                      <img
                        src={coverImageUrl(res.coverUrl)}
                        referrerPolicy={coverReferrerPolicy(res.coverUrl)}
                        alt=""
                        className="y-src-card-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="y-src-cover-placeholder" />
                    )}

                    <div className="y-src-card-info">
                      <strong className="y-src-card-title">{res.title}</strong>
                      <div className="y-src-card-tags">
                        <span className="y-src-source-tag">{sourceName(res.sourceId)}</span>
                        <span className="y-src-active-tag">Activa</span>
                      </div>
                    </div>

                    <div className="y-src-card-actions">
                      <button
                        type="button"
                        className="y-src-btn-danger"
                        title="Quitar esta fuente si te equivocaste"
                        onClick={() => void unlinkSource(res)}
                      >
                        <Trash2 size={14} />
                        <span>Quitar</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="y-src-empty-state">
                <p>No hay fuentes vinculadas manualmente para esta obra todavía.</p>
                <button
                  type="button"
                  className="nyomiri-btn nyomiri-btn-neutral"
                  style={{ marginTop: 12 }}
                  onClick={() => setActiveTab('search')}
                >
                  <Search size={14} />
                  <span>Buscar fuentes para vincular</span>
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
